import type { RouteObject } from "react-router-dom";
import { useRoutes, Navigate, Route } from "react-router-dom";

import { AuthContent } from "@sso/components/AuthContent";
import Payment from "@sso/components/Payment";
import PaymentReturn from "@sso/components/PaymentReturn";
import { TableLayoutContent } from "@sso/components/TableLayoutContent";
import { PathKeys } from "@sso/constant";
import { APIAccounts } from "@sso/layouts/APIAccounts";
import { BannedIP } from "@sso/layouts/BannedIP";
import { BilledOperations } from "@sso/layouts/BilledOperations";
import { BilledOperationSummary } from "@sso/layouts/BilledOperationSummary";
import Licences from "@sso/layouts/Licences";
import LicenceTypes from "@sso/layouts/LicenceTypes";
import Orders from "@sso/layouts/Orders";
import { Organisations } from "@sso/layouts/Organisations";
import Pricing from "@sso/layouts/Pricing";
import ProductLicences from "@sso/layouts/ProductLicences";
import { Products } from "@sso/layouts/Products";
import { Profile } from "@sso/layouts/Profile";
import { ChangePassword } from "@sso/layouts/Profile/ChangePassword";
import { EditProfile } from "@sso/layouts/Profile/EditProfile";
import { TraccUsages } from "@sso/layouts/TraccUsages";
import { Users } from "@sso/layouts/Users";
import { ForgotPassword } from "@sso/pages/auth/ForgotPassword";
import { ResetPassword } from "@sso/pages/auth/ResetPassword";
import { SignIn } from "@sso/pages/auth/SignIn";
import { SignUp } from "@sso/pages/auth/SignUp";
import { DeclineResetPassword } from "@sso/pages/DeclineResetPassword";
import { EmailConfirmation } from "@sso/pages/EmailConfirmation";
import { Home } from "@sso/pages/Home";
import { PurchaseLicence } from "@sso/pages/PurchaseLicence";
import TraccWebMapPurchase from "@sso/pages/Tracc/Purchase/TraccWebMapPurchase";

const MainRoutes: RouteObject = {
  path: PathKeys.ROOT,
  element: <Home />,
  children: [
    { index: true, element: <Products /> },
    {
      path: PathKeys.PROFILE,
      element: <Profile />,
      children: [
        { path: PathKeys.EDIT_PROFILE, element: <EditProfile /> },
        { path: PathKeys.CHANGE_PASSWORD, element: <ChangePassword /> },
      ],
    },
    {
      path: PathKeys.MANAGEMENT,
      element: <TableLayoutContent />,
      children: [
        { path: PathKeys.ORGANISATIONS, element: <Organisations /> },
        { path: PathKeys.USERS, element: <Users /> },
        { path: PathKeys.API_ACCOUNTS, element: <APIAccounts /> },
        { path: PathKeys.BANNED_IP, element: <BannedIP /> },
        { path: PathKeys.BILLED_OPERATIONS, element: <BilledOperations /> },
        { path: PathKeys.BILLED_OPERATION_SUMMARY, element: <BilledOperationSummary /> },
        { path: PathKeys.LICENCES, element: <Licences /> },
        { path: PathKeys.PRODUCT_LICENCES, element: <ProductLicences /> },
        { path: `${PathKeys.LICENCE_TYPES}`, element: <LicenceTypes /> },
        { path: PathKeys.PRICING, element: <Pricing /> },
        { path: PathKeys.ORDERS, element: <Orders /> },
      ],
    },
    {
      path: PathKeys.PAYMENT,
      element: <TableLayoutContent />,
      children: [{ index: true, element: <Payment /> }],
    },
    {
      path: PathKeys.RETURN,
      element: <TableLayoutContent />,
      children: [{ index: true, element: <PaymentReturn /> }],
    },
    { path: "*", element: <Products /> },
    {
      path: PathKeys.SALES,
      element: <TableLayoutContent />,
      children: [{ path: PathKeys.TRACC_USAGE, element: <TraccUsages /> }],
    },
    {
      path: PathKeys.TRACC,
      element: <TableLayoutContent />,
      children: [{ path: PathKeys.TRACC_WEB_MAP_PURCHASE, element: <TraccWebMapPurchase /> }],
    }
  ],
};

const AuthRoutes: RouteObject = {
  path: PathKeys.AUTH_ROOT,
  element: <AuthContent />,
  children: [
    { index: true, element: <Navigate to={PathKeys.SIGN_IN} replace /> },
    { path: PathKeys.SIGN_IN, element: <SignIn /> },
    { path: PathKeys.SIGN_UP, element: <SignUp /> },
    { path: PathKeys.FORGOT_PASSWORD, element: <ForgotPassword /> },
    { path: PathKeys.RESET_PASSWORD, element: <ResetPassword /> },
    { path: "*", element: <SignIn /> },
  ],
};

const OtherRoute: RouteObject[] = [
  // { path: PathKeys.LOADING_APP, element: <LoadingApp /> },
  { path: PathKeys.EMAIL_CONFIRMATION, element: <EmailConfirmation /> },
  { path: PathKeys.DECLINE_RESET_PASSWORD, element: <DeclineResetPassword /> },
];

const PurchaseRoute: RouteObject[] = [{ path: PathKeys.PURCHASE_LICENCE, element: <PurchaseLicence /> }];

export const Routes = () => {
  return useRoutes([MainRoutes, AuthRoutes, ...OtherRoute, ...PurchaseRoute]);
};
